import { EditOutlined } from '@ant-design/icons'
import { message, Modal } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { ProForm, ProFormText } from '@ant-design/pro-components'
import { useInterval, useRequest } from 'ahooks'
import { useT } from '@/common/utils/translation'
import { outLogin, sendCode, updatePassword } from '@/services/common.js'
import { useRootContext } from '@/common/RootContext.jsx'
import { textHyposensitization } from '@/common/utils/tools.js'
import { myCookie } from '@/common/utils/cookie.js'
import CaptchaModal from '@/pages/components/CaptchaModal'

const EditPassword = ({ onClickEditPassword }) => {
  let t = useT()
  const rootContentData = useRootContext()
  const [open, setOpen] = useState(false)
  const [count, setCount] = useState(60)
  const [interval, setInterval] = useState(undefined)
  const [codeError, setCodeError] = useState(false)
  const formRef = useRef()
  const [messageApi, contextHolder] = message.useMessage()
  const [captchaModalVisible, setCaptchaModalVisible] = useState(false)
  const [captchaRefreshTrigger, setCaptchaRefreshTrigger] = useState(0)
  const captchaButtonRef = useRef(null)
  const clear = useInterval(() => {
    setCount(count - 1)
  }, interval)

  useEffect(() => {
    if (count === 0) {
      setInterval(undefined)
      clear()
    }
  }, [count])

  const { runAsync: runSendCode, loading: sendCodeLoading } = useRequest(sendCode,
    { manual: true })
  const {
    runAsync: runUpdatePassword,
    loading: updatePasswordLoading,
  } = useRequest(updatePassword,
    {
      manual: true, onSuccess: (res) => {
        if (res.success) {
          messageApi.open({ type: 'success', content: t('components_change_password') })
          outLogin().then((res) => {
            if (res.success) {
              message.success(t('components_exit_successfully'))
              myCookie.delete('kerqu-admin-token')
              sessionStorage.removeItem('kerquTraderToken')
              rootContentData.dispatchSetLoginStatus(false)
              rootContentData.dispatchSetLoginVisible(true)
            } else {
              message.error(res.message)
            }
          })
          setOpen(false)
        } else {
          if (res.code === 'VERIFY_CODE_ERROR') {
            setCodeError(true)
          } else {
            messageApi.open({ type: 'error', content: res.message })
          }
        }
      },
    })

  const getCapture = async () => {
    if (sendCodeLoading) {
      return
    }

    const phone = import.meta.env.VITE_LANG === 'en' ? rootContentData?.userInfo?.admin?.email : rootContentData?.userInfo?.admin?.phone
    if (!phone) {
      messageApi.open({
        type: 'error',
        content: '请先登录',
      })
      return
    }
    
    // 显示图片验证码弹窗
    setCaptchaModalVisible(true)
  }

  // 图片验证码验证成功后发送短信
  const handleCaptchaSuccess = async (captchaData) => {
    // 立即关闭弹窗，给用户即时反馈
    setCaptchaModalVisible(false)
    
    try {
      const phone = import.meta.env.VITE_LANG === 'en' ? rootContentData?.userInfo?.admin?.email : rootContentData?.userInfo?.admin?.phone
      const params = {
        phone: phone,
        code: captchaData.code,
        key: captchaData.key,
        token: captchaData.token, // 添加盲水印token
        sceneCode: 'RESET_PASSWORD'
      }

      setInterval(1000)
      setCount(60)
      await runSendCode(params)
      
      // 验证成功
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: error.message || '发送失败，请重试',
      })
      // 发送失败时重新打开弹窗并自动刷新验证码
      setCaptchaModalVisible(true)
      setCaptchaRefreshTrigger(prev => prev + 1)
    }
  }

  // 关闭图片验证码弹窗
  const handleCaptchaCancel = () => {
    setCaptchaModalVisible(false)
  }

  const onOk = () => {
    formRef.current?.validateFieldsReturnFormatValue?.().then(async (values) => {
      await runUpdatePassword(values)
    })
  }

  useEffect(() => {
    if (!open) {
      formRef.current?.setFieldsValue({ code: '', password: '' })
    }
  }, [open])

  return (
    <>
      {contextHolder}
      <div style={{ fontSize: '0.88rem', paddingTop: '6px', paddingInline: '10px' }} className="text-center cursor-pointer" onClick={() => {
        setOpen(true)
        onClickEditPassword()
      }}>
        <EditOutlined/>&nbsp;{t('components_change_password')}
      </div>
      {open ? <Modal destroyOnClose centered open={open}
                     title={t('components_change_password')}
                     onCancel={() => { setOpen(false) }} onOk={onOk}
                     cancelText={t('cancel')} okText={t('ok')}
                     confirmLoading={updatePasswordLoading}
                     zIndex={1000}>
        <div style={{ marginTop: '24px' }}>
          {import.meta.env.VITE_LANG === 'en' ? `${t('components_send_verification_code_to_email')}: ` : t('components_send_verification_code_to_mobile_phone')}
          {import.meta.env.VITE_LANG === 'en' ? rootContentData?.userInfo?.admin?.email :
            textHyposensitization(
              rootContentData?.userInfo?.admin?.phone,
              3,
              8,
            )
          }
        </div>
        <ProForm formRef={formRef} style={{ marginTop: '24px' }} submitter={{
          resetButtonProps: { style: { display: 'none' } },
          submitButtonProps: { style: { display: 'none' } },
        }}>
          <ProFormText
            name="code"
            label={t('components_verification_code')}
            rules={[
              {
                required: true,
                message: t(
                  'components_verification_code_is_required'),
              }]}
            fieldProps={{
              suffix: (interval ? <div>{count}{t(
                'components_second')}</div> : <a
                ref={captchaButtonRef}
                style={{ color: '#7860FE' }}
                onClick={() => getCapture()}>{t(
                'components_get_verification_code')}</a>),
            }}
            validateStatus={codeError ? 'error' : ''}
            help={codeError ? t('components_verification_code_error') : ''}
            placeholder=""
          />
          <ProFormText.Password name="password"
                                label={t('components_new_password')} rules={[
            {
              required: true,
              message: t('components_new_password_is_required'),
            }]}/>
        </ProForm>
      </Modal> : null}
      
      {/* CaptchaModal 必须在 Modal 外部渲染 */}
      {captchaModalVisible && (
        <CaptchaModal
          visible={captchaModalVisible}
          onOk={handleCaptchaSuccess}
          onCancel={handleCaptchaCancel}
          triggerElement={captchaButtonRef.current} // 传递按钮DOM元素
          refreshTrigger={captchaRefreshTrigger} // 传递刷新触发器
        />
      )}
    </>
  )
}

export default EditPassword